import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import {
  Landmark, Sparkles, Boxes, GraduationCap, Building2, TrendingUp, Rocket,
  Check, Target, Network, Layers, Handshake, ArrowRight, Linkedin, Mail,
} from "lucide-react";

const SERVE = [
  { icon: Landmark, label: "Venture Capital Firms" },
  { icon: Sparkles, label: "Accelerators & Incubators" },
  { icon: Boxes, label: "Venture Studios" },
  { icon: GraduationCap, label: "Universities" },
  { icon: Building2, label: "Corporate Innovation Teams" },
  { icon: TrendingUp, label: "Economic Development Organizations" },
  { icon: Rocket, label: "Venture-backed Startups" },
];

const RECRUIT = [
  "Accelerator Directors",
  "Executive Directors",
  "Startup CEOs",
  "Entrepreneurs-in-Residence",
  "Venture Partners",
  "Corporate Innovation Leaders",
  "Fractional Executives",
  "Board Members",
  "Innovation Program Managers",
];

const DIFFERENT = [
  { icon: Target, title: "Innovation-Focused", body: "We work exclusively in the innovation economy — search and advisory, nothing else." },
  { icon: Network, title: "Deep Ecosystem Relationships", body: "We understand the people, organizations, and careers that drive innovation." },
  { icon: Layers, title: "Search + Advisory", body: "We recruit exceptional leaders and help build the programs and ecosystems they lead." },
  { icon: Handshake, title: "Long-Term Partnerships", body: "We aim to become a trusted talent and advisory partner, not just fill one position." },
];

const HELP = [
  { icon: Target, title: "Executive Search", body: "Recruiter-led search for the leaders who build and scale innovation organizations." },
  { icon: Layers, title: "Advisory Services", body: "Accelerator and program design, ecosystem strategy, program audits, mentor networks, and fractional leadership." },
];

const VALUES = [
  "Relationships First",
  "Integrity Always",
  "Confidentiality Matters",
  "Excellence Over Volume",
  "Innovation Through People",
];

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function OurStory() {
  return (
    <div className="min-h-screen bg-white text-[#09090B]">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-20 lg:py-28">
          <p className="overline mb-4">Our Story</p>
          <motion.h1 {...fade} className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
            Built for the people who<br /><span className="text-primary">build what's next.</span>
          </motion.h1>
        </div>
      </section>

      {/* Story */}
      <section className="border-t border-black/10 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline">How we started</p>
            <h2 className="font-heading font-black text-3xl mt-3">Building the leaders, programs, and ecosystems behind innovation.</h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              Throughout our careers, we've worked alongside founders, investors, accelerators, universities,
              corporate innovation teams, and economic development organizations. Two challenges surfaced time and
              again: finding experienced leaders who truly understand the innovation ecosystem — and designing the
              programs and strategies that help those organizations grow.
            </p>
            <p>
              Traditional recruiting firms often lack the specialized networks needed to hire these roles, general
              professional platforms make it hard to identify leaders with real ecosystem experience, and traditional
              consultants rarely have firsthand experience building innovation programs.
            </p>
            <p className="text-[#09090B] font-medium">Innovation Talent Partners was created to change that.</p>
            <p>
              We pair recruiter-led executive search with strategic advisory — helping organizations hire exceptional
              talent and build world-class programs and ecosystems, while giving innovation professionals a place to
              discover meaningful opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <p className="overline mb-4">What We Do</p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl max-w-2xl">Specialized search and advisory across the innovation ecosystem.</h2>
          <div className="grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10 mt-12">
            {HELP.map((h, i) => (
              <motion.div key={h.title} {...fade} transition={{ delay: i * 0.06 }} className="bg-white p-8">
                <h.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                <h3 className="font-heading font-bold text-lg mt-5">{h.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{h.body}</p>
              </motion.div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-12 mt-12">
            {/* Who we serve */}
            <div>
              <p className="overline mb-4">Who We Serve</p>
              <div className="space-y-px bg-black/10 border border-black/10">
                {SERVE.map((s, i) => (
                  <motion.div key={s.label} {...fade} transition={{ delay: i * 0.04 }}
                    className="bg-white p-4 flex items-center gap-3 hover:bg-[#F4F4F5] transition-colors">
                    <s.icon className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                    <span className="font-medium">{s.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            {/* Who we recruit */}
            <div>
              <p className="overline mb-4">Leadership We Recruit</p>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                {RECRUIT.map((r, i) => (
                  <motion.div key={r} {...fade} transition={{ delay: i * 0.04 }}
                    className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-1" strokeWidth={2.5} />
                    <span>{r}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why we're different */}
      <section className="border-t border-black/10 bg-[#09090B] text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <p className="overline mb-4 text-white/50">Why We're Different</p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl max-w-2xl">A different kind of talent partner.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-12">
            {DIFFERENT.map((d, i) => (
              <motion.div key={d.title} {...fade} transition={{ delay: i * 0.08 }}
                className="bg-[#09090B] p-8 hover:bg-[#111114] transition-colors">
                <d.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                <h3 className="font-heading font-bold text-lg mt-5">{d.title}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{d.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline">Our Values</p>
            <h2 className="font-heading font-black text-3xl mt-3">What we stand for.</h2>
          </div>
          <div className="lg:col-span-8">
            <div className="space-y-px bg-black/10 border border-black/10">
              {VALUES.map((v, i) => (
                <motion.div key={v} {...fade} transition={{ delay: i * 0.06 }}
                  className="bg-white p-6 flex items-center gap-4 hover:bg-[#F4F4F5] transition-colors">
                  <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-heading font-bold text-lg">{v}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet the founder */}
      <section className="border-t border-black/10 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <p className="overline">Meet the Founder</p>
            <div className="mt-5 aspect-square w-full max-w-[16rem] overflow-hidden bg-[#09090B]">
              <img src="https://customer-assets-jt897jd0.emergentagent.net/job_talent-search-crm/artifacts/gbu9vurq_headshot.png" alt="Kendrick Alridge, Founder" className="h-full w-full object-cover" data-testid="founder-photo" />
            </div>
            <h2 className="font-heading font-black text-2xl mt-6">Kendrick Alridge</h2>
            <p className="text-sm text-muted-foreground mt-1">Founder, Innovation Talent Partners</p>
            <div className="flex flex-col gap-2 mt-4">
              <a href="https://www.linkedin.com/in/kendrickalridge/" target="_blank" rel="noreferrer" data-testid="founder-linkedin" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                <Linkedin className="h-4 w-4" /> Connect on LinkedIn
              </a>
              <a href="mailto:kendrick@innovationtalentpartners.com" data-testid="founder-email" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                <Mail className="h-4 w-4" /> kendrick@innovationtalentpartners.com
              </a>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-5 text-lg text-muted-foreground leading-relaxed">
            <p className="text-[#09090B] font-medium">Hi, I'm Kendrick Alridge, Founder of Innovation Talent Partners.</p>
            <p>
              Over the past decade, I've had the privilege of working alongside founders, investors, accelerators,
              universities, corporate innovation teams, and economic development organizations. Throughout that
              journey, I've helped build founder communities, design startup programs, recruit leaders, foster
              strategic partnerships, and support organizations dedicated to advancing innovation.
            </p>
            <p>
              One thing has become clear: great innovation ecosystems are built by great people, thoughtful strategy,
              and strong leadership.
            </p>
            <p>
              Throughout my career, I've helped founders navigate accelerator programs, connected entrepreneurs with
              investors and mentors, developed initiatives that supported emerging companies, and advised
              organizations on how to strengthen their innovation communities. Whether working with early-stage
              startups or established innovation organizations, my passion has always been helping people and
              organizations achieve meaningful, lasting impact.
            </p>
            <p>
              As I worked across the innovation ecosystem, I noticed a recurring challenge. Many organizations were
              trying to launch or grow accelerators, innovation programs, and entrepreneurship initiatives while also
              struggling to recruit leaders with the specialized experience needed to make those programs successful.
              Traditional consulting firms often lacked firsthand ecosystem experience, and traditional executive
              search firms rarely understood the unique talent required to lead these organizations.
            </p>
            <p className="text-[#09090B] font-medium">That's why I founded Innovation Talent Partners.</p>
            <p>
              Our mission is simple: to help organizations build exceptional teams and world-class innovation
              ecosystems.
            </p>
            <p>
              Today, we partner with startups, venture capital firms, accelerators, universities, corporate innovation
              teams, nonprofits, and economic development organizations through executive search and strategic advisory
              services. Whether we're recruiting a Managing Director, designing an accelerator program, strengthening a
              founder pipeline, building a mentor network, or advising on ecosystem strategy, our focus is the same —
              helping organizations create lasting impact through people.
            </p>
            <p>
              We're also building something bigger. We envision Innovation Talent Partners becoming the trusted talent
              and advisory partner for the innovation economy — a place where founders, investors, innovation leaders,
              and organizations connect to build stronger companies, stronger communities, and stronger ecosystems.
            </p>
            <p>
              At the end of the day, this isn't just about filling positions or launching programs. It's about creating
              the people, partnerships, and infrastructure that allow innovation to thrive.
            </p>
            <p>I look forward to building that future with you.</p>
            <div className="pt-4 border-t border-black/10">
              <p className="font-heading font-black text-xl text-[#09090B]">— Kendrick Alridge</p>
              <p className="text-sm text-muted-foreground">Founder, Innovation Talent Partners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Looking ahead */}
      <section className="border-t border-black/10 bg-primary text-white overflow-hidden relative">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 py-20 relative grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="overline text-white/60">Looking Ahead</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl mt-3 leading-tight">The Future of Innovation Talent.</h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg leading-relaxed">
            <p>
              Today, we help organizations recruit exceptional leaders through executive search and strengthen their
              programs through strategic advisory.
            </p>
            <p className="text-white/80">
              Tomorrow, we're building a platform where innovation professionals can create verified profiles,
              explore executive opportunities, connect with organizations, and grow their careers — while giving
              employers a trusted destination to discover and engage the next generation of innovation leaders.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl">Let's build your team — and your ecosystem.</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
            Whether you're hiring for a defining leadership role, designing a new program, or exploring your next move, we'd love to talk.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/for-clients" data-testid="story-cta-client" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-primary text-white hover:bg-[#09090B] transition-colors">
              Request a search <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/advisory-services" data-testid="story-cta-advisory" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-black/15 hover:bg-black/5 transition-colors">
              Explore advisory
            </Link>
            <Link to="/for-candidates" data-testid="story-cta-candidate" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-black/15 hover:bg-black/5 transition-colors">
              Join as a candidate
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
